import { useState, useEffect, useCallback, useRef } from 'react';
import { getBackendUrl } from '../config/apiConfig';
import { cachedFetch } from '../utils/fetchCache';
import { isModeratorOrAdmin } from '../utils/permissions';
import { isNativePlatform } from '../services/biometricAuth';
import logger from '../utils/logger';

/**
 * Hook: useContributionPopup
 *
 * Business logic (gates evaluated in order):
 *   1. User is logged in.
 *   2. User is NOT admin or moderator  (staff roles excluded).
 *   3. Site-wide contributions are enabled.
 *   4. This user is NOT individually disabled by an admin.
 *   5. `membership.hasAccess` is false — activated members are skipped
 *      entirely (no donation asks on a paid membership).
 *
 * If all gates pass:
 *   - `shouldShowContribution` becomes true ONLY for non-activated members
 *     (membership.hasAccess === false) → the activation banner shows.
 *   - Activated members never see banners or the popup.
 *   - The popup never auto-opens — it opens imperatively via openPopup()
 *     (banner click, `force-contribution-popup` event, membership gates).
 */

// Small settle delay so the eligibility check doesn't block page render.
const CHECK_DELAY_MS = 1500;
// Set when the user closes the popup — suppresses system-initiated opens
// (e.g. membership-required errors) for the rest of the session. Deliberate
// clicks via openPopup() still always open.
const SESSION_POPUP_DISMISSED = 'contribution_popup_dismissed_session';

const useContributionPopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [shouldShowContribution, setShouldShowContribution] = useState(false);
  const [loading, setLoading] = useState(true);
  const [needsActivation, setNeedsActivation] = useState(false);
  const [contributionConfig, setContributionConfig] = useState(null);
  const popupTimeoutRef = useRef(null);

  const checkEligibility = useCallback(async () => {
    // Reset at the top so re-runs can REVOKE eligibility (e.g., after a
    // successful payment the member is now activated).
    setShouldShowContribution(false);
    setNeedsActivation(false);

    // Google Play policy: no payment/contribution surfaces in the native app
    if (isNativePlatform()) {
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      // Gate: staff roles are excluded (also skips the API call).
      if (isModeratorOrAdmin()) {
        setLoading(false);
        return;
      }

      // Shares the TopBar contribution-status cache — TopBar fetches the same
      // endpoint on mount, so the popup gate reuses it instead of a duplicate.
      const data = await cachedFetch(
        `contributionStatus:${localStorage.getItem('username')}`,
        async () => {
          const res = await fetch(`${getBackendUrl()}/api/contributions/contribution-status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          return res.ok ? res.json() : null;
        },
        60000
      );
      if (!data) {
        setLoading(false);
        return;
      }
      if (!data.success) {
        setLoading(false);
        return;
      }

      // Config is needed by <ContributionPopup /> regardless of eligibility.
      setContributionConfig(data.popupConfig);

      // Gate: site-level enabled (strict True).
      if (!data.siteEnabled) {
        setLoading(false);
        return;
      }
      // Gate: admin disabled this particular user.
      if (data.userDisabledByAdmin) {
        setLoading(false);
        return;
      }

      // Gate: backend says no popup needed (active membership, admin grant, threshold met).
      if (data.showPopup === false) {
        logger.debug('🔔 Contribution: backend says no popup needed');
        setShouldShowContribution(false);
        setLoading(false);
        return;
      }

      // Banner eligibility is driven purely by membership access:
      //   - NOT activated → activation banner shows (popup opens only on click)
      //   - activated     → no banner, no popup (no money asks for paid members)
      // The popup never auto-opens; openPopup() is imperative-only.
      const hasSearchAccess = Boolean(data?.membership?.hasAccess);
      setNeedsActivation(!hasSearchAccess);
      setShouldShowContribution(!hasSearchAccess);
    } catch (err) {
      logger.error('🔔 Contribution: eligibility check failed', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const scheduleCheck = useCallback((delay) => {
    if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current);
    popupTimeoutRef.current = setTimeout(() => {
      checkEligibility();
      popupTimeoutRef.current = null;
    }, delay);
  }, [checkEligibility]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) scheduleCheck(CHECK_DELAY_MS);

    const handleUserLoggedIn = () => scheduleCheck(CHECK_DELAY_MS);

    // Fired by payment-success handlers. Re-checks eligibility so the banner
    // disappears immediately after a contribution (silence window now active).
    const handleContributionMade = () => {
      scheduleCheck(500);
    };

    window.addEventListener('userLoggedIn', handleUserLoggedIn);
    window.addEventListener('contributionMade', handleContributionMade);
    return () => {
      if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current);
      window.removeEventListener('userLoggedIn', handleUserLoggedIn);
      window.removeEventListener('contributionMade', handleContributionMade);
    };
  }, [scheduleCheck]);

  const closePopup = useCallback(() => {
    sessionStorage.setItem(SESSION_POPUP_DISMISSED, '1');
    setShowPopup(false);
  }, []);

  // Imperative open (e.g., banner "Contribute Now" click). Ignores the
  // session-dismissed flag so a deliberate click always opens the popup.
  // No-op in the native app — payment UI is hidden there (Play policy).
  const openPopup = useCallback(() => {
    if (isNativePlatform()) return;
    setShowPopup(true);
  }, []);

  return {
    showPopup,
    shouldShowContribution,
    closePopup,
    openPopup,
    loading,
    contributionConfig,
      needsActivation,
  };
};

export default useContributionPopup;
